import { useCustomFilters } from "@features/cx/shared/utils/filters";
import { GenericTable } from "@shared/components/GenericTable";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllScheduleServiceOrderHistory } from "../../../../hooks";
import { HistoryFilterPanel } from "../../filters/HistoryFilterPanel";

interface ScheduleHistoryTabProps {
  scheduleId: string;
}

interface HistoryItem {
  id: string;
  name: string;
  assetId: string;
  assetName: string;
  location: string;
  type: "Preventive" | "Corrective" | "Inspection";
  completedOn: string;
  completedBy: string;
  totalCost: number;
  laborHours: number;
  failureMode: string;
  sop: any;
}

const getTypeColor = (type: string) => {
  switch (type) {
    case "Preventive":
      return "bg-green-100 text-green-800";
    case "Corrective":
      return "bg-red-100 text-red-800";
    case "Inspection":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const ScheduleHistoryTab: React.FC<ScheduleHistoryTabProps> = ({
  scheduleId,
}) => {
  const navigate = useNavigate();
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();
  const [currentPage, setCurrentPage] = useState(1);

  // Memoize the filter configuration to prevent infinite re-renders
  const filterConfig = useMemo(
    () => ({
      paramNames: {
        assetType: "assetType",
        serviceType: "serviceType",
        failureMode: "failureMode",
        dataFrom: "dataFrom",
        dataTo: "dataTo",
        search: "query",
        page: "page",
      },
      defaults: {
        page: DEFAULT_PAGE_NUMBER,
        limit: PAGE_SIZE,
        assetType: "all",
        serviceType: "all",
        failureMode: "all",
        dataFrom: "",
        dataTo: "",
        search: "",
      },
      parsers: {
        assetType: (value: string) =>
          value === "all" ? undefined : (value as "pump" | "valve" | "motor"),
        serviceType: (value: string) =>
          value === "all"
            ? undefined
            : (value as "Preventive" | "Corrective" | "Inspection"),
        failureMode: (value: string) =>
          value === "all"
            ? undefined
            : (value as "bearing" | "seal" | "electrical" | "none"),
        dataFrom: (value: string) => value.trim() || undefined,
        dataTo: (value: string) => value.trim() || undefined,
        search: (value: string) => value.trim(),
      },
      serializers: {
        assetType: (value: string | undefined) => value || "all",
        serviceType: (value: string | undefined) => value || "all",
        failureMode: (value: string | undefined) => value || "all",
        dataFrom: (value: string | undefined) => value || "",
        dataTo: (value: string | undefined) => value || "",
        search: (value: string) => value.trim(),
      },
    }),
    []
  );

  // Use the configurable URL filter utility
  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const { data: historyData, isLoading } = useGetAllScheduleServiceOrderHistory(
    scheduleId,
    page,
    PAGE_SIZE,
    filters.search_data,
    filters.status,
    filters.date_from,
    filters.date_to
  );

  // Pagination calculations based on API data
  const pagination = useMemo(() => {
    if (!historyData) {
      return {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: PAGE_SIZE,
        startItem: 0,
        endItem: 0,
      };
    }

    const totalPages = Math.ceil((historyData.totalCount || 0) / PAGE_SIZE);
    const startItem = (page - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(page * PAGE_SIZE, historyData.totalCount || 0);

    return {
      currentPage: page,
      totalPages,
      totalItems: historyData.totalCount || 0,
      itemsPerPage: PAGE_SIZE,
      startItem,
      endItem,
    };
  }, [historyData, page]);

  // Action handlers
  const handleViewSODetails = (item: HistoryItem) => {
    console.log("Viewing SO details for:", item.id);
    // Navigate to service order details page
    // navigate(`/service-orders/${item.id}`);
  };
  const getServiceTypeFromAssociation = (
    sopAssociationTypeDisplay: string
  ): string => {
    const association = sopAssociationTypeDisplay;

    if (association?.includes("Asset")) return "Asset";
    if (association?.includes("Facility")) return "Facility";
    if (association?.includes("System")) return "System";
    if (association?.includes("Network")) return "Network";

    // Default fallback
    return "Asset";
  };

  const handleViewDetails = (row: any) => {
    const serviceType = getServiceTypeFromAssociation(
      row.sop.sopAssociationTypeDisplay
    );
    const status = row.statusDisplay?.toLowerCase();

    // Determine the route based on status
    let routePrefix = "";

    switch (status) {
      case "created":
      case "overdue":
        routePrefix = "pending";
        break;
      case "assigned":
      case "accepted":
      case "in-progress":
      case "in progress":
        routePrefix = "assigned";
        break;
      case "completed":
      case "rejected":
        routePrefix = "completed";
        break;
      case "canceled":
      case "cancelled":
      case "approved":
        routePrefix = "history";
        break;
      default:
        // Default to pending for unknown statuses
        routePrefix = "pending";
    }

    navigate(
      `/ax/service-order/landing/${routePrefix}/view/${serviceType}/${row.id}`
    );
  };

  const handleSOIdClick = (item: HistoryItem) => {
    console.log("SO ID clicked:", item.id);
    handleViewSODetails(item);
  };

  const handleAssetClick = (item: HistoryItem) => {
    console.log("Asset clicked:", item.assetId);
    // Navigate to asset details page
    // navigate(`/assets/${item.assetId}`);
  };

  const handleExport = () => {
    console.log("Exporting history data");
    // Implement export functionality
  };

  // Table actions configuration
  const actions: TableAction<HistoryItem>[] = [
    {
      label: "View SO Details",
      onClick: (item: HistoryItem) => handleViewDetails(item),
    },
    {
      label: "View Work Summary Report",
      disabled: (row: any) =>
        row.statusDisplay?.toLowerCase() !== "completed" &&
        row.statusDisplay?.toLowerCase() !== "approved",
      onClick: (item: HistoryItem) => handleViewDetails(item),
    },
  ];

  // Define table columns
  const columns: TableColumn<HistoryItem>[] = [
    {
      key: "jobCardNo",
      header: "SO ID",
      headerTooltip: "Unique, system-generated ID",
    },
    {
      key: "sopName",
      header: "SO Name",
      render(value, row) {
        return <p>{row.sop.name}</p>;
      },
      headerTooltip: "The name of the service order.",
    },
    {
      key: "sopAssociationTypeDisplay",
      header: "Service Association",
      render(value, row) {
        return <p>{row.sop.sopAssociationTypeDisplay}</p>;
      },
      headerTooltip:
        "The type of entity the order is for (Asset, System, Facility, Network).",
    },
    {
      key: "requestAccountNo",
      header: "Ref ID/Name",
      sortable: true,
      type: "custom",
      render: (value: string, row: any) => (
        <div>
          <div>{value ?? "NA"}</div>
          <div className="text-sm text-gray-500">
            {row.requestUsername ?? "NA"}
          </div>
        </div>
      ),
      headerTooltip: "The ID and name of the specific entity.",
    },
    {
      key: "operationTypeDisplay",
      header: "Type",
      render(value, row) {
        return <p>{row.sop.operationTypeDisplay}</p>;
      },
      headerTooltip:
        "The category of work or maintenance operation that this work order covers (e.g., Preventive Maintenance, Repair, Inspection, Emergency Response).",
    },
    {
      key: "completedDate",
      header: "Completed Date",
      sortable: true,
      headerTooltip: "The date the service order was completed.",
    },
    {
      key: "fieldForce",
      header: "Completed By",
      sortable: true,
      render: (value: any) => value?.name || "NA",
      headerTooltip: "The field force member assigned to the order.",
    },
    {
      key: "totalActualCost",
      header: "Total Cost",
      sortable: true,
      headerTooltip: "The total cost of the service order.",
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      headerTooltip: "The current status of the service order.",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="page-title mb-2">Service Order History</CardTitle>
        {/* <p className="page-subtitle mb-2">
          To date, this schedule has generated{" "}
          <strong>
            {historyData?.totalCompletedOrders || 0} completed Service Orders
          </strong>{" "}
          with a total recorded cost of{" "}
          <strong>
            {currency}
            {historyData?.totalRecordedCost || 0}
          </strong>
          .
        </p> */}
      </CardHeader>
      <CardContent>
        <GenericTable
          data={historyData?.results || []}
          columns={columns}
          rowKey="id"
          searchPlaceholder="Search by SO Name, Asset, Completed By..."
          searchParamName="query"
          searchDebounceMs={300}
          onSearch={handleSearch}
          pagination={pagination}
          onPageChange={handlePageChange}
          actions={actions}
          actionsType="dropdown"
          filterPanel={
            <HistoryFilterPanel
              currentFilters={filters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          onExport={handleExport}
          emptyMessage="No service order history found"
          loading={isLoading}
        />
      </CardContent>
    </Card>
  );
};
